import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { UnreachableCaseError } from 'ts-essentials';
import { theme } from '../../../styles/theme';
import { IStyled } from '../../../styles/types';
import { CHART_ERROR_MESSAGES } from '../constants/utils';
import { ChartViewType, IChartError } from '../types';
import { useKobolErrorInfo } from '../../../utils/hooks';
import { SvgIcon } from '../../Icon';

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
`;

const Heading = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 500;
  color: ${theme.colors.secondary900};
`;

const Description = styled.div`
  font-size: 12px;
  color: ${theme.colors.secondary800};
`;

type IErrorChartInfoProps = IChartError & IStyled;

const ErrorChartInfo = ({ className, ...chartView }: IErrorChartInfoProps) => {
  const { type } = chartView;
  const { getKobolTitleAndDescription } = useKobolErrorInfo();

  let title: ReactNode;
  let description: ReactNode;

  switch (type) {
    case ChartViewType.NoData:
    case ChartViewType.Cancelled:
    case ChartViewType.PermissionError:
    case ChartViewType.FallbackError:
      // Use default values from the CHART_ERROR_MESSAGES mapping
      title = CHART_ERROR_MESSAGES[type].title;
      description = CHART_ERROR_MESSAGES[type].description;
      break;

    case ChartViewType.KobolError:
      const kobolErrorInfo = getKobolTitleAndDescription(chartView);
      title = kobolErrorInfo.title;
      description = kobolErrorInfo.description;
      break;

    case ChartViewType.CustomError:
      title = chartView.title || CHART_ERROR_MESSAGES[type].title;
      description =
        chartView.description || CHART_ERROR_MESSAGES[type].description;
      break;

    default:
      throw new UnreachableCaseError(type);
  }

  return (
    <Container className={className}>
      <SvgIcon
        name="triangle-exclamation-outline"
        width={32}
        height={32}
        customFill={theme.colors.mainBlue}
      />
      <Heading>{title}</Heading>
      <Description>{description}</Description>
    </Container>
  );
};

export default ErrorChartInfo;
